import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { matchApi } from '../services/api';
import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { CircularProgress, Box } from '@mui/material';

// Define the context type
interface ContentBlocksSchemaContextType {
  schema: RJSFSchema | null;
  uiSchemas: Record<string, UiSchema>;
  loading: boolean;
  error: Error | null;
}

// Create the context with default values
const ContentBlocksSchemaContext = createContext<ContentBlocksSchemaContextType>({
  schema: null,
  uiSchemas: {},
  loading: true,
  error: null,
});

// Define the provider props
interface ContentBlocksSchemaProviderProps {
  children: ReactNode;
}

// Create custom UI schemas for different block types
const createUISchemas = (): Record<string, UiSchema> => ({
  Paragraph: {
    text: {
      'ui:widget': 'textarea',
      'ui:options': { rows: 5 },
    },
  },
  HTML: {
    html: {
      'ui:widget': 'textarea',
      'ui:options': { rows: 10 },
    },
  },
  // Add more UI schemas as needed
});

/**
 * Provider component that fetches the content blocks schema from the API
 * and provides it to child components via context
 */
export const ContentBlocksSchemaProvider: React.FC<ContentBlocksSchemaProviderProps> = ({ children }) => {
  const [schema, setSchema] = useState<RJSFSchema | null>(null);
  const [uiSchemas, setUiSchemas] = useState<Record<string, UiSchema>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        setLoading(true);
        const response = await matchApi.getContentBlocksSchema();
        setSchema(response.data);
        
        // Create UI schemas based on the received schema
        setUiSchemas(createUISchemas());
      } catch (err) {
        console.error('Failed to fetch content blocks schema:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.error('Error loading schema:', error);
  }

  return (
    <ContentBlocksSchemaContext.Provider value={{ schema, uiSchemas, loading, error }}>
      {children}
    </ContentBlocksSchemaContext.Provider>
  );
};

/**
 * Custom hook to use the content blocks schema context
 */
export const useContentBlocksSchema = () => useContext(ContentBlocksSchemaContext);

export default ContentBlocksSchemaProvider;
